/**
 * Conversion of retained PSD text metadata into native editable text layers.
 * Used by the PSD codec at import time and by the headless promoteText op.
 * Layout is approximate by design: the PSD text engine is not reimplemented;
 * the box is measured from the text and the position derives from the PSD
 * baseline transform. Rendering fidelity depends on font availability.
 */
import type { TextLayer } from '../core/model';
import { rgbToHex } from '../tooling/pixel-ops';
import { measureTextWidth } from '../tooling/text-measure';
import { createTextLayer } from '../vector/model';

export interface PsdTextMetadata {
  text?: string;
  transform?: number[];
  style?: { font?: string; fontSize?: number; tracking?: number; leading?: number; autoLeading?: boolean; fillColor?: unknown };
  justification?: string;
  /** Paragraph auto-leading ratio (Photoshop's default is 1.2). */
  autoLeadingRatio?: number;
  shapeType?: 'point' | 'box';
  /** Paragraph text box [left, top, right, bottom] in text space. */
  boxBounds?: number[];
}

/** Maipo's native text cannot be smaller than this (project validation). */
const MIN_TEXT_SIZE = 1;
/** Photoshop's type size when a style sheet leaves it unset. */
const PSD_DEFAULT_FONT_SIZE = 12;

/** A damaged file can store non-finite doubles; they would make the project unsaveable. */
function finiteOr(value: number | undefined, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function textScale(transform: readonly number[] | undefined): { x: number; y: number } {
  const x = typeof transform?.[0] === 'number' && transform[0] > 0 ? transform[0] : 1;
  const y = typeof transform?.[3] === 'number' && transform[3] > 0 ? transform[3] : 1;
  return { x, y };
}

type PsdText = NonNullable<import('ag-psd').Layer['text']>;

/**
 * What the rebuilt editable text cannot carry over from the PSD. Each item is
 * a visible difference; naming them keeps the import report honest instead of
 * implying that only font availability can change the result.
 */
export function textApproximations(text: PsdText): string[] {
  const losses: string[] = [];
  const base = text.style ?? {};
  const runs = text.styleRuns ?? [];
  const keys = ['fontSize', 'fauxBold', 'fauxItalic', 'underline', 'strikethrough', 'fontCaps'] as const;
  const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);
  const reference = { ...base, ...runs[0]?.style };
  const mixed = runs.slice(1).some(({ style }) => (style.font?.name !== undefined && style.font.name !== reference.font?.name)
    || (style.fillColor !== undefined && !same(style.fillColor, reference.fillColor))
    || keys.some((key) => style[key] !== undefined && style[key] !== reference[key]));
  if (mixed) losses.push(`mixed character styles (${runs.length} runs) were flattened to the first run's font, size and colour`);
  const transform = text.transform ?? [];
  if (Math.abs(transform[1] ?? 0) > 1e-3 || Math.abs(transform[2] ?? 0) > 1e-3) losses.push('rotation or skew was dropped');
  else if (Math.abs((transform[0] ?? 1) - (transform[3] ?? 1)) > 1e-3) losses.push('non-uniform horizontal scaling was dropped');
  const warp = text.warp;
  if (warp?.style && warp.style !== 'none' && (warp.value || warp.perspective || warp.perspectiveOther)) losses.push(`the “${warp.style}” warp was dropped`);
  if (text.orientation === 'vertical') losses.push('vertical text was set horizontally');
  const styles = [base, ...runs.map((run) => run.style)];
  const decorations = [
    styles.some((style) => style.fauxBold) ? 'faux bold' : '',
    styles.some((style) => style.fauxItalic) ? 'faux italic' : '',
    styles.some((style) => style.underline) ? 'underline' : '',
    styles.some((style) => style.strikethrough) ? 'strikethrough' : '',
    styles.some((style) => style.fontCaps) ? 'all caps / small caps' : '',
    styles.some((style) => (style.horizontalScale ?? 1) !== 1 || (style.verticalScale ?? 1) !== 1) ? 'character scaling' : '',
  ].filter(Boolean);
  if (decorations.length) losses.push(`${decorations.join(', ')} ${decorations.length === 1 ? 'is' : 'are'} not reproduced`);
  const fontSize = base.fontSize ?? runs[0]?.style.fontSize ?? PSD_DEFAULT_FONT_SIZE;
  if (fontSize * textScale(transform).y < MIN_TEXT_SIZE) losses.push(`text smaller than ${MIN_TEXT_SIZE}px was enlarged to ${MIN_TEXT_SIZE}px`);
  return losses.length ? [`Not reproduced in the editable text: ${losses.join('; ')}.`] : [];
}

/** PostScript-name prefixes whose installed CSS family name differs. */
export const PSD_FONT_ALIASES: Record<string, string> = {
  NeueHaasDisplay: 'Neue Haas Grotesk Display Pro',
};

export function psdColorToHex(color: unknown): string | null {
  if (!color || typeof color !== 'object') return null;
  const record = color as Record<string, unknown>;
  const channel = (byte: unknown, fraction: unknown): number | null =>
    typeof byte === 'number' ? byte : typeof fraction === 'number' ? fraction * 255 : null;
  const r = channel(record.r, record.fr);
  const g = channel(record.g, record.fg);
  const b = channel(record.b, record.fb);
  if (r === null || g === null || b === null) return null;
  return rgbToHex(r, g, b);
}

function resolveFamily(base: string): string {
  const spaced = base.replace(/([a-z\d])([A-Z])/g, '$1 $2').trim();
  const candidates = [PSD_FONT_ALIASES[base], base, spaced === base ? undefined : spaced]
    .filter((candidate): candidate is string => Boolean(candidate));
  if (typeof document !== 'undefined' && document.fonts) {
    for (const candidate of candidates) {
      try {
        if (document.fonts.check(`16px "${candidate}"`)) return candidate;
      } catch {
        // font matching unavailable; fall through to the best guess
      }
    }
  }
  return candidates[0] ?? base;
}

/**
 * The CSS generic family closest to a PostScript font name. Without one, an
 * uninstalled design font (Helvetica Neue, Futura, Proxima Nova…) falls back
 * to the browser default, which is a serif face.
 */
export function genericFamilyForPsdFont(name: string): 'serif' | 'sans-serif' | 'monospace' {
  if (/mono|courier|consol|menlo|code/i.test(name)) return 'monospace';
  if (/sans|grotesk|gothic/i.test(name)) return 'sans-serif';
  if (/serif|times|georgia|garamond|baskerville|caslon|bodoni|didot|minion|palatino|cambria|bookman|century|cochin|hoefler|playfair|merriweather|lora|charter/i.test(name)) return 'serif';
  return 'sans-serif';
}

export function fontFromPsdName(name: string | undefined): { family: string; weight: number } {
  if (!name) return { family: 'Inter, sans-serif', weight: 400 };
  const match = /^(.*?)(?:[-\s](Thin|ExtraLight|Light|Regular|Book|Medium|SemiBold|Semibold|Bold|ExtraBold|Black|Heavy|Roman))?(Italic|Oblique)?$/i.exec(name);
  const base = (match?.[1] ?? name).replace(/MT$|PS$/, '').trim() || name;
  const weights: Record<string, number> = { thin: 100, extralight: 200, light: 300, regular: 400, book: 400, roman: 400, medium: 500, semibold: 600, bold: 700, extrabold: 800, black: 900, heavy: 900 };
  const weight = weights[(match?.[2] ?? 'regular').toLowerCase()] ?? 400;
  return { family: `${resolveFamily(base)}, ${genericFamilyForPsdFont(name)}`, weight };
}

/** Builds an editable text layer from retained PSD text metadata. */
export function textLayerFromPsdMetadata(meta: PsdTextMetadata, options: { name: string; boundsWidth?: number; pixelBounds?: { left: number; right: number } }): TextLayer {
  const value = meta.text ?? '';
  const scale = textScale(meta.transform);
  const scaleY = scale.y;
  // Project validation requires at least 1px; smaller text is reported. A
  // non-finite size or scale from a damaged file falls back to the default.
  const scaled = Math.round((meta.style?.fontSize ?? PSD_DEFAULT_FONT_SIZE) * scaleY * 100) / 100;
  const size = Math.max(MIN_TEXT_SIZE, finiteOr(scaled, PSD_DEFAULT_FONT_SIZE));
  const { family, weight } = fontFromPsdName(meta.style?.font);
  const color = psdColorToHex(meta.style?.fillColor) ?? '#000000';
  // With Auto leading on, Photoshop ignores the stored leading value (often a
  // stale 0.01 or 150+) and spaces lines by the paragraph's auto ratio.
  const autoLeading = meta.style?.autoLeading === true || (meta.style?.autoLeading === undefined && !meta.style?.leading);
  const lineHeight = Math.max(0.5, Math.min(3, autoLeading || !meta.style?.leading
    ? meta.autoLeadingRatio ?? 1.2
    : meta.style.leading / (meta.style.fontSize ?? PSD_DEFAULT_FONT_SIZE)));
  const tracking = meta.style?.tracking ? Math.round((meta.style.tracking / 1000) * size * 100) / 100 : 0;
  const justification = meta.justification ?? '';
  const align = justification.includes('center') ? 'center' as const : justification.includes('right') ? 'right' as const : 'left' as const;
  const measured = measureTextWidth(value, family, weight, size, tracking);
  const [boxLeft, boxTop, boxRight, boxBottom] = meta.boxBounds ?? [];
  // Paragraph (box) text wraps inside its box, whose top-left is the text
  // transform origin offset by boxBounds; point text is anchored at its first
  // baseline instead.
  const paragraph = meta.shapeType === 'box' && [boxLeft, boxTop, boxRight, boxBottom].every((entry) => typeof entry === 'number' && Number.isFinite(entry))
    && boxRight! > boxLeft! && boxBottom! > boxTop!;
  const boxWidth = paragraph
    ? Math.max(MIN_TEXT_SIZE, Math.round((boxRight! - boxLeft!) * scale.x * 100) / 100)
    : Math.max(50, Math.ceil(Math.max(measured * 1.05 + 4, options.boundsWidth ?? 0)));

  const layer = createTextLayer();
  layer.name = options.name;
  layer.text = {
    ...layer.text,
    value,
    box: { width: boxWidth, height: Math.max(40, Math.ceil(size * lineHeight * (value.split('\n').length + 0.5))), autoHeight: true },
    style: { family, weight, size, lineHeight, tracking, align, color },
  };
  // The PSD text transform origin is the first baseline; approximate the
  // layout box top by backing off one em. Horizontally the rasterized
  // preview's pixel bounds are the exact glyph edges — when available they
  // beat the transform anchor, respecting the alignment side.
  if (paragraph) {
    layer.transform = [1, 0, 0, 1, finiteOr(meta.transform?.[4], 0) + boxLeft! * scale.x, finiteOr(meta.transform?.[5], 0) + boxTop! * scaleY];
    return layer;
  }
  let tx = finiteOr(meta.transform?.[4], 0);
  if (options.pixelBounds) {
    if (align === 'right') tx = options.pixelBounds.right - boxWidth;
    else if (align === 'center') tx = (options.pixelBounds.left + options.pixelBounds.right - boxWidth) / 2;
    else tx = options.pixelBounds.left;
  }
  const ty = finiteOr(meta.transform?.[5], 0) - size;
  layer.transform = [1, 0, 0, 1, tx, ty];
  return layer;
}
